import React, { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { catalogTokens, parseApiError } from "./VendorCatalogTheme";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: `${catalogTokens.radius.md}px` },
};

const GROUPS = [
  { value: "OCCASION", label: "Occasion" },
  { value: "CELEBRATION", label: "Celebration" },
];

// SCM/Admin-only screen for managing the curated Collection/Theme taxonomy — the picker on the
// upload/edit form only reads this list, it has no way to add to it, so this is the only place
// new Occasion/Celebration tags (e.g. "Holi Gifts") can be created.
const VendorCatalogCollectionManager = () => {
  const { instance } = useMsal();
  const { account } = useAuth();

  const [collections, setCollections] = useState([]);
  const [newName, setNewName] = useState("");
  const [newGroup, setNewGroup] = useState("OCCASION");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const authedRequest = useCallback(
    async (url, method, body) => {
      const token = await getApiToken(instance, account);
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const text = await res.text();
      return text ? JSON.parse(text) : {};
    },
    [instance, account]
  );

  const fetchCollections = useCallback(() => {
    if (!account) return;
    authedRequest(`${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/collections`, "GET")
      .then(setCollections)
      .catch((err) => setError(err.message || "Failed to load collections"));
  }, [account, authedRequest]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!newName.trim()) return;

    setSaving(true);
    try {
      await authedRequest(`${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/collections`, "POST", {
        name: newName.trim(),
        group: newGroup,
      });
      setNewName("");
      setSuccess("Collection added.");
      fetchCollections();
    } catch (err) {
      setError(err.message || "Failed to add collection");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (collection) => {
    const newValue = window.prompt("Rename collection:", collection.name);
    if (!newValue || !newValue.trim() || newValue.trim() === collection.name) return;

    setError(null);
    try {
      await authedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/collections/${collection.id}`,
        "PUT",
        { name: newValue.trim(), group: collection.group }
      );
      fetchCollections();
    } catch (err) {
      setError(err.message || "Failed to rename collection");
    }
  };

  const handleDelete = async (collection) => {
    setError(null);
    try {
      await authedRequest(`${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/collections/${collection.id}`, "DELETE");
      fetchCollections();
    } catch (err) {
      // Backend returns a 409 with a clear message when a catalog still references this tag.
      setError(err.message || "Failed to delete collection");
    }
  };

  const renderGroup = (groupValue, groupLabel) => {
    const items = collections.filter((c) => c.group === groupValue);
    return (
      <Box sx={{ flex: 1, minWidth: 260 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{groupLabel}</Typography>
        <List
          dense
          sx={{
            borderRadius: `${catalogTokens.radius.md}px`,
            border: `1px solid ${catalogTokens.color.border}`,
            minHeight: 64,
            bgcolor: catalogTokens.color.surface,
          }}
        >
          {items.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="None yet"
                primaryTypographyProps={{ sx: { color: catalogTokens.color.textSecondary } }}
              />
            </ListItem>
          ) : (
            items.map((c) => (
              <ListItem
                key={c.id}
                secondaryAction={
                  <>
                    <IconButton size="small" onClick={() => handleEdit(c)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(c)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <ListItemText primary={c.name} />
              </ListItem>
            ))
          )}
        </List>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <Typography variant="subtitle1" fontWeight={800} sx={{ color: catalogTokens.color.textPrimary, mb: 0.25 }}>
        Manage Collections / Themes
      </Typography>
      <Typography variant="body2" sx={{ color: catalogTokens.color.textSecondary, mb: 2.5 }}>
        Add, rename, or remove the Occasion/Celebration tags available when uploading or editing a catalog.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: `${catalogTokens.radius.md}px` }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: `${catalogTokens.radius.md}px` }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Box component="form" onSubmit={handleAdd} sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="New collection name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          size="small"
          sx={{ ...fieldSx, flex: 1, minWidth: 200 }}
        />
        <FormControl size="small" sx={{ ...fieldSx, minWidth: 160 }}>
          <InputLabel>Group</InputLabel>
          <Select value={newGroup} label="Group" onChange={(e) => setNewGroup(e.target.value)}>
            {GROUPS.map((g) => (
              <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={saving || !newName.trim()}
          sx={{
            borderRadius: `${catalogTokens.radius.md}px`,
            textTransform: "none",
            fontWeight: 700,
            bgcolor: catalogTokens.color.primary,
            "&:hover": { bgcolor: catalogTokens.color.primaryDark },
          }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Add"}
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {renderGroup("OCCASION", "Occasion")}
        {renderGroup("CELEBRATION", "Celebration")}
      </Box>
    </Box>
  );
};

export default VendorCatalogCollectionManager;
